/**
 * Authoritative monthly Stage Finance ledger.
 *
 * North Star (every month):
 *   Opening + sponsor cash actually contributed + bank/partner draw
 *   + buyer cash actually received − payment due − repayments = Closing
 *
 * Closing(N) = Opening(N+1). Every P&L cost paid exactly once by completion.
 * Negative cash is a real funding gap — no automatic SPV plug.
 *
 * Sponsor equity + fees form the opening funding pot. Monthly SPV inflows are
 * only explicit scheduled capital calls — never capital-matched drips.
 *
 * One loop only. Strategies decide funding mechanics; this module applies the cash equation.
 * Timing comes from cash events → cost/sales schedules.
 */

import { hydrateAssumptions, MODES } from './defaults.js';
import { costsPaidInMonth } from './buildCostSchedule.js';
import { stageAtMonth } from './buildProgramme.js';
import { capitalCallsByMonth } from './buildCashEvents.js';
import { n, nearlyEqual } from './math.js';
import { cashInNote, paymentDueNote } from './paymentEvents.js';
import { computeRepaymentRunwayReserve } from './repaymentRunway.js';
import { quotePartMonthDecision } from './strategies/quotePart.js';
import {
  computeVatConstructionFacilityRequired,
  vatMonthDecision,
} from './strategies/venteATerme.js';

/**
 * @returns {{
 *   incomplete: boolean,
 *   mode: string,
 *   months: object[],
 *   openingEquity: number,
 *   facilityAnalysis: object,
 *   totals: object,
 * }}
 */
export function buildLedger({
  pnl,
  programme,
  costSchedule,
  salesSchedule,
  cashEvents = [],
  rawAssumptions = {},
}) {
  const assumptions = hydrateAssumptions(rawAssumptions);
  const mode = assumptions.mode;

  if (!pnl.complete) {
    return {
      incomplete: true,
      incompleteReasons: pnl.incompleteReasons || [],
      mode,
      months: [],
      openingEquity: 0,
      facilityAnalysis: {},
      totals: {},
    };
  }

  const decisionFn = mode === MODES.quotePart ? quotePartMonthDecision : vatMonthDecision;

  const facilityAnalysis = mode === MODES.venteATerme
    ? computeVatConstructionFacilityRequired({
      pnl,
      programme,
      assumptions,
      costSchedule,
      salesSchedule,
    })
    : {
      constructionFacilityRequired: 0,
      constructionFacilitySecured: 0,
      remainingBankFundingGap: 0,
      facilityMustBeAvailableMonth: null,
      alreadySecuredLandFacility: 0,
    };

  // Equity + Fees are one sponsor funding pot available at opening (M0/M1).
  const feesFunding = Math.max(0, n(pnl.feesFunding ?? pnl.upfrontFees));
  const openingEquity = Math.max(0, pnl.sponsorEquity) + feesFunding;
  const state = {
    restrictedBuyerCash: 0,
    bankDrawn: 0,
    landFacilityDrawn: 0,
    constructionFacilityDrawn: 0,
    sharedFacilityDrawn: 0,
    securedConstructionFacilityDrawn: 0,
    projectedConstructionFacilityDrawn: 0,
    partnerDrawn: 0,
    bankDebtOutstanding: 0,
    landDebtOutstanding: 0,
    constructionDebtOutstanding: 0,
    partnerDebtOutstanding: 0,
    cumulativeSpvContributions: 0,
    cumulativeLandCosts: 0,
    cumulativeConstructionCosts: 0,
    spvAdvancedConstruction: 0,
    cumulativeDistributions: 0,
    cumulativeCapitalMatched: 0,
  };

  let openingCash = openingEquity;
  const months = [];
  // Capital calls come from cash events (SoT), not raw assumption arrays.
  const capitalCalls = capitalCallsByMonth(cashEvents);

  for (let month = 1; month <= programme.totalMonths; month += 1) {
    const costs = costsPaidInMonth(costSchedule, month - 1);
    const salesRow = salesSchedule.months[month - 1];

    // Only explicit capital-call events — never invent matched feas/financing SPV inflows.
    const scheduledRaise = Math.max(0, n(capitalCalls[month]));
    const matchedCapital = 0;
    const spvContribution = scheduledRaise;

    const decision = decisionFn({
      month,
      programme,
      pnl,
      assumptions,
      salesRow,
      costs,
      costSchedule,
      openingCash,
      spvContribution,
      availableSpvCash: openingCash + spvContribution,
      state,
      // Vente à terme structurally requires construction credit. Model the
      // required-but-not-yet-committed portion so cash survival is realistic,
      // while facilityAnalysis keeps that P&L funding gap visible.
      plannedConstructionFacility: mode === MODES.venteATerme
        ? Math.max(0, facilityAnalysis.remainingBankFundingGap || 0)
        : 0,
    });

    const bankDraw = Math.max(0, decision.bankDraw);
    const partnerDraw = Math.max(0, decision.partnerDraw);
    const spvCostRelief = Math.max(0, decision.spvCostRelief || 0);
    const spvCostsPaid = Math.max(0, costs.total - spvCostRelief);
    const constructionReimbursement = Math.max(0, decision.constructionReimbursement || 0);

    // Restricted deposits (VAT) — tracked, never enter the cash equation.
    const restrictedDelta = decision.restrictedBuyerCashDelta || 0;
    state.restrictedBuyerCash = Math.max(0, state.restrictedBuyerCash + restrictedDelta);

    // Apply draws to debt first. Track land vs construction for repayment waterfall.
    const landFacilityDrawNow = Math.max(0, decision.landFacilityDraw || 0);
    const constructionFacilityDrawNow = Math.max(0, decision.constructionFacilityDraw || 0);
    state.bankDebtOutstanding += bankDraw;
    state.landDebtOutstanding += landFacilityDrawNow;
    // Construction + shared/projected/partner construction-like draws feed construction debt.
    const constructionDebtDraw = Math.max(0, bankDraw - landFacilityDrawNow);
    state.constructionDebtOutstanding += constructionDebtDraw;
    state.partnerDebtOutstanding += partnerDraw;
    state.bankDrawn += bankDraw;
    state.landFacilityDrawn += landFacilityDrawNow;
    state.constructionFacilityDrawn += constructionFacilityDrawNow;
    state.sharedFacilityDrawn += Math.max(0, decision.sharedFacilityDraw || 0);
    state.securedConstructionFacilityDrawn += Math.max(
      0,
      decision.securedConstructionFacilityDraw || 0,
    );
    state.projectedConstructionFacilityDrawn += Math.max(
      0,
      decision.projectedConstructionFacilityDraw || 0,
    );
    state.partnerDrawn += partnerDraw;

    // Quote-part land settlement: reduce land debt first, then remaining bank debt;
    // route any excess land proceeds to usable SPV cash (never drop value on the floor).
    const landSettlementRequest = Math.max(0, decision.buyerDirectLandSettlement || 0);
    let buyerDirectLandSettlement = 0;
    if (landSettlementRequest > 0 && state.bankDebtOutstanding > 0) {
      buyerDirectLandSettlement = Math.min(landSettlementRequest, state.bankDebtOutstanding);
      const againstLand = Math.min(buyerDirectLandSettlement, state.landDebtOutstanding);
      state.landDebtOutstanding -= againstLand;
      const againstConstruction = Math.min(
        buyerDirectLandSettlement - againstLand,
        state.constructionDebtOutstanding,
      );
      state.constructionDebtOutstanding -= againstConstruction;
      state.bankDebtOutstanding -= buyerDirectLandSettlement;
    }
    const landSettlementToSpvCash = Math.max(0, landSettlementRequest - buyerDirectLandSettlement);

    // usableBuyerCash includes construction reimbursement (strategy) + excess land proceeds.
    const usableBuyerCash =
      Math.max(0, decision.usableBuyerCash) + landSettlementToSpvCash;

    // Cash equation before repayments.
    let closingCash =
      openingCash +
      spvContribution +
      bankDraw +
      partnerDraw +
      usableBuyerCash -
      spvCostsPaid;

    let bankRepayment = 0;
    let partnerRepayment = 0;
    let sponsorDistribution = 0;

    // Opportunistic waterfall from true surplus only: look ahead and reserve enough
    // SPV cash for all known net payments until the next confirmed usable cash-in or
    // confirmed facility draw (not future signed sales, restricted deposits, or
    // projected construction credit). Then repay land debt first, then construction.
    // Never repay into a deficit. Remaining debt is settled at delivery.
    const reserveFloor = computeRepaymentRunwayReserve({
      afterMonth: month,
      programme,
      costSchedule,
      assumptions,
      pnl,
      state,
      capitalCalls,
      salesPctAlreadyAchieved: decision.salesPct || 0,
    });
    let surplus = Math.max(0, closingCash - reserveFloor);
    if (surplus > 1e-9 && (state.landDebtOutstanding > 1e-9 || state.constructionDebtOutstanding > 1e-9)) {
      const repayLand = Math.min(surplus, state.landDebtOutstanding);
      if (repayLand > 1e-9) {
        closingCash -= repayLand;
        bankRepayment += repayLand;
        state.landDebtOutstanding -= repayLand;
        state.bankDebtOutstanding = Math.max(0, state.bankDebtOutstanding - repayLand);
        surplus -= repayLand;
      }
      const repayConstruction = Math.min(surplus, state.constructionDebtOutstanding);
      if (repayConstruction > 1e-9) {
        closingCash -= repayConstruction;
        bankRepayment += repayConstruction;
        state.constructionDebtOutstanding -= repayConstruction;
        state.bankDebtOutstanding = Math.max(0, state.bankDebtOutstanding - repayConstruction);
        surplus -= repayConstruction;
      }
    }

    // Delivery: settle any remaining bank/partner debt from SPV cash. Debt never disappears off-account.
    if (decision.repayAtDelivery) {
      const repayBank = Math.min(Math.max(0, closingCash), state.bankDebtOutstanding);
      closingCash -= repayBank;
      bankRepayment += repayBank;
      state.bankDebtOutstanding -= repayBank;
      const landShare = Math.min(repayBank, state.landDebtOutstanding);
      state.landDebtOutstanding -= landShare;
      state.constructionDebtOutstanding = Math.max(
        0,
        state.constructionDebtOutstanding - (repayBank - landShare),
      );

      const repayPartner = Math.min(Math.max(0, closingCash), state.partnerDebtOutstanding);
      closingCash -= repayPartner;
      partnerRepayment += repayPartner;
      state.partnerDebtOutstanding -= repayPartner;

      if (assumptions.distributeAtCompletion) {
        const capitalStillOut = Math.max(0, pnl.capitalInvested - state.cumulativeDistributions);
        const capitalReturn = Math.min(Math.max(0, closingCash), capitalStillOut);
        const afterCapital = closingCash - capitalReturn;
        const profitDist = Math.min(Math.max(0, afterCapital), Math.max(0, pnl.netProfit));
        sponsorDistribution = capitalReturn + profitDist;
        closingCash -= sponsorDistribution;
        state.cumulativeDistributions += sponsorDistribution;
      }
    }

    const repayments = bankRepayment + partnerRepayment;
    const equationClosing =
      openingCash +
      spvContribution +
      bankDraw +
      partnerDraw +
      usableBuyerCash -
      spvCostsPaid -
      repayments -
      sponsorDistribution;

    const fundingGap = Math.max(0, -closingCash);
    const bufferGap = Math.max(0, assumptions.minCashBuffer - closingCash);

    state.cumulativeSpvContributions += spvContribution;
    state.cumulativeCapitalMatched += matchedCapital;
    state.cumulativeLandCosts += decision.landCostNow || 0;
    state.cumulativeConstructionCosts += decision.constructionCostNow || 0;

    // Track SPV-advanced construction for quote-part reimbursement.
    if (mode === MODES.quotePart) {
      // Only the pure Build contract transfers to the buyer. The reserve remains
      // a promoter/SPV risk and can never be hidden as buyer-paid construction.
      const grossBuild = costs.detail.constructionCost || 0;
      state.spvAdvancedConstruction += Math.max(0, grossBuild - spvCostRelief);
      state.spvAdvancedConstruction = Math.max(
        0,
        state.spvAdvancedConstruction - constructionReimbursement,
      );
    }

    const buyerTxn = decision.buyerTransaction || null;

    months.push({
      month,
      stage: stageAtMonth(month, programme),
      openingSpvCash: openingCash,
      spvContribution,
      scheduledSpvRaise: scheduledRaise,
      capitalMatchedContribution: matchedCapital,
      securedBankDraw: bankDraw,
      landFacilityDraw: Math.max(0, decision.landFacilityDraw || 0),
      constructionFacilityDraw: Math.max(0, decision.constructionFacilityDraw || 0),
      projectedConstructionFacilityDraw: Math.max(
        0,
        decision.projectedConstructionFacilityDraw || 0,
      ),
      securedPartnerDraw: partnerDraw,
      securedBankPartnerIn: bankDraw + partnerDraw,
      usableBuyerCash,
      usableSpvProceeds: buyerTxn ? buyerTxn.usableSpvProceeds : usableBuyerCash,
      restrictedBuyerCash: state.restrictedBuyerCash,
      restrictedBuyerCashDelta: restrictedDelta,
      spvCostsPaid,
      costDetail: { ...costs.detail },
      spvCostRelief,
      bankRepayment,
      partnerRepayment,
      repayments,
      sponsorDistribution,
      closingSpvCash: closingCash,
      bankDebtOutstanding: state.bankDebtOutstanding,
      partnerDebtOutstanding: state.partnerDebtOutstanding,
      salesPct: decision.salesPct,
      constructionPct: decision.constructionPct,
      salesSecured: pnl.salesRevenue * decision.salesPct,
      buyerDirectConstruction: Math.max(0, decision.buyerDirectConstruction || 0),
      buyerDirectLandSettlement,
      landSettlementToSpvCash,
      constructionReimbursement,
      buyerTransaction: buyerTxn,
      fundingGap,
      bufferGap,
      unmetBankConditions: decision.unmetBankConditions || [],
      landDebtOutstanding: state.landDebtOutstanding,
      constructionDebtOutstanding: state.constructionDebtOutstanding,
      paymentDueNote: paymentDueNote(costs.detail, {
        month,
        programme,
        assumptions,
        isDelivery: month === programme.deliveryMonth,
        costEvents: null,
      }),
      cashInNote: cashInNote({
        spvContribution,
        landFacilityDraw: landFacilityDrawNow,
        constructionFacilityDraw: constructionFacilityDrawNow,
        securedPartnerDraw: partnerDraw,
        usableBuyerCash,
      }),
      equationBalances: nearlyEqual(equationClosing, closingCash, 0.02),
    });

    openingCash = closingCash;
  }

  const totalSpvContributions = openingEquity + state.cumulativeSpvContributions;
  const last = months[months.length - 1] || null;
  const scheduledTotal = Object.values(capitalCalls).reduce((s, v) => s + Math.max(0, n(v)), 0);
  const sponsorFundingPot = openingEquity + scheduledTotal;

  // Quote-part buyer conservation across the project — applied destinations only.
  // Land request = debt settlement applied + excess routed to SPV cash.
  const buyerConservation = months.reduce(
    (acc, m) => {
      const t = m.buyerTransaction;
      if (!t) return acc;
      acc.revenue += t.revenueDelta;
      acc.usableSpv += t.usableSpvProceeds;
      acc.landSettledAgainstDebt += m.buyerDirectLandSettlement || 0;
      acc.landExcessToSpv += m.landSettlementToSpvCash || 0;
      acc.directBuild += t.buyerDirectCurrentConstruction;
      acc.reimburse += t.constructionReimbursement;
      return acc;
    },
    {
      revenue: 0,
      usableSpv: 0,
      landSettledAgainstDebt: 0,
      landExcessToSpv: 0,
      directBuild: 0,
      reimburse: 0,
    },
  );
  const buyerSplitTotal =
    buyerConservation.usableSpv +
    buyerConservation.landSettledAgainstDebt +
    buyerConservation.landExcessToSpv +
    buyerConservation.directBuild +
    buyerConservation.reimburse;

  return {
    incomplete: false,
    mode,
    months,
    openingEquity,
    facilityAnalysis,
    totals: {
      totalSpvContributions,
      capitalInvested: pnl.capitalInvested,
      // Sponsor funding pot = equity + fees (+ explicit capital calls). No longer
      // forced to equal P&L Capital Invested (which still includes feas + financing).
      spvContributionsReconcile: nearlyEqual(totalSpvContributions, sponsorFundingPot, 1),
      sponsorFundingPot,
      cumulativeSpvInflows: state.cumulativeSpvContributions,
      cumulativeCapitalMatched: state.cumulativeCapitalMatched,
      cumulativeDistributions: state.cumulativeDistributions,
      finalClosingCash: last ? last.closingSpvCash : openingEquity,
      finalBankDebt: last ? last.bankDebtOutstanding : 0,
      totalProjectedConstructionFacilityDraw: state.projectedConstructionFacilityDrawn,
      totalCostsConsumed: months.reduce((s, m) => s + m.spvCostsPaid + m.spvCostRelief, 0),
      totalBuyerDirectConstruction: months.reduce((s, m) => s + m.buyerDirectConstruction, 0),
      totalBuyerDirectLandSettlement: months.reduce((s, m) => s + m.buyerDirectLandSettlement, 0),
      totalConstructionReimbursement: months.reduce((s, m) => s + m.constructionReimbursement, 0),
      grossSalesValue: pnl.salesRevenue,
      quotePartDirectConstructionContract: mode === MODES.quotePart ? pnl.constructionCost : 0,
      quotePartSpvSaleEntitlement: mode === MODES.quotePart
        ? Math.max(0, pnl.salesRevenue - pnl.constructionCost)
        : pnl.salesRevenue,
      totalCashReceivedBySpv: months.reduce((s, m) => s + m.usableBuyerCash, 0),
      buyerConservation,
      buyerTransactionConserves: mode !== MODES.quotePart
        || nearlyEqual(buyerConservation.revenue, buyerSplitTotal, 0.05),
    },
  };
}
